import PropTypes from 'prop-types';
import { useAssetDisplay, useTenantDisplay, AssetIdUtils } from '@cyberint/common-utils';
import { t as translate } from '@cyberint/common-alerts';

// Preset formatters consume the enriched `view`: `typeLabel` is the translated
// asset type (e.g. `domain` -> "Domain") and `customerLabel` is the resolved
// tenant name (falls back to the raw customer id). Function formatters receive
// the same object, so they still have the raw `type_id` / `customer_id` plus
// the resolved labels.
const formatters = {
  name: a => a?.name,
  name_with_type: a => (a?.name && a?.typeLabel ? `${a.name} (${a.typeLabel})` : a?.name),
  name_with_customer: a => (a?.name && a?.customerLabel ? `${a.name} - ${a.customerLabel}` : a?.name),
  full: a => {
    if (!a?.name) return null;
    if (a.typeLabel && a.customerLabel) return `${a.name} (${a.typeLabel}) - ${a.customerLabel}`;
    if (a.typeLabel) return `${a.name} (${a.typeLabel})`;
    return a.name;
  },
  id: (a, id) => id
};

// Legacy ids (`type/customer/name`) yield non-empty parts via the sync split,
// so they render correctly on the first paint. UUID ids fall through with
// `type_id === ''` and `name === assetId` — we treat that as "unresolved"
// and prefer `loadingFallback` (typically a skeleton) over flashing the UUID.
const isUnresolved = (asset, assetId) => !asset?.type_id && asset?.name === assetId;

export const AssetDisplay = ({ assetId, format = 'name', fallback, loadingFallback }) => {
  const { data } = useAssetDisplay(assetId);
  const parts = AssetIdUtils.getAssetDisplayParts(assetId);
  const asset = data || { type_id: parts.type, customer_id: parts.customer, name: parts.name };
  const { name: customerName } = useTenantDisplay(asset?.customer_id);

  if (!assetId) return fallback || null;
  if (loadingFallback != null && isUnresolved(asset, assetId)) return loadingFallback;

  const typeLabel = asset?.type_id ? translate(`asset_type.${asset.type_id}`, undefined, asset.type_id) : '';
  const customerLabel = customerName || asset?.customer_id || '';
  const view = { ...asset, typeLabel, customerLabel };

  if (typeof format === 'function') return format(view, assetId) || assetId;
  const formatter = formatters[format] || formatters.name;
  return formatter(view, assetId) || assetId;
};

AssetDisplay.propTypes = {
  assetId: PropTypes.string,
  format: PropTypes.oneOfType([
    PropTypes.oneOf(['name', 'name_with_type', 'name_with_customer', 'full', 'id']),
    PropTypes.func
  ]),
  fallback: PropTypes.node,
  loadingFallback: PropTypes.node
};
